import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { ElementsWorkspace } from '../../components/elements/ElementsWorkspace.js';
import {
  useLocalElementsAddress,
  useRoutedElementsAddress,
  type ElementsAddress,
  type ElementsNavigate,
} from '../../components/elements/address.js';

/**
 * THE DETAILED BREAKDOWN WORKFLOW — cast, extras, props, costumes, vehicles,
 * effects, scene by scene (owner brief, 2026-10-29; Production Workspace 2.0,
 * 2026-11-05: one screen in three panes, `ElementsWorkspace`).
 *
 * Nothing here decides a location, and nothing on the Locations pages marks a
 * sheet reviewed: the two workflows share the confirmed scenes and the one AI
 * reading, and nothing else.
 *
 * DONE IS NAVIGATION. Completion is the server's — every scene's sheet marked
 * reviewed — so "Back to workflows" records nothing; the choice screen then
 * shows where this workflow stands, beside Locations.
 *
 * THE PAGE'S STATE IS ITS ADDRESS (`?scene=&filter=&q=&cat=&ie=&tod=&char=&sort=
 * &item=`, and the production plan's `?view=&req=`): the route passes it in
 * through `ElementsStepAtAddress`. Rendered without one, the page keeps the same
 * state itself.
 */
export function ElementsStep({
  projectId,
  onDone,
  address,
  onAddressChange,
  onOpenLocationRequirement,
}: {
  projectId: string;
  /** Kept for the callers that pass it; the shell already names the production. */
  projectTitle?: string;
  onDone: () => void;
  /** The route's address; absent, the page owns it. */
  address?: ElementsAddress;
  onAddressChange?: ElementsNavigate;
  /** Opens a location need of the production plan in the Locations workflow. */
  onOpenLocationRequirement?: (requirementId: string) => void;
}) {
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: ['current-script', projectId],
    queryFn: () => api.currentScript(projectId),
  });
  const [own, ownNavigate] = useLocalElementsAddress();
  const here = address ?? own;
  const navigate = onAddressChange ?? ownNavigate;

  if (current.isPending) {
    return (
      <p className="mm" style={{ fontSize: 13 }} data-testid="elements-step-loading">
        Loading the screenplay…
      </p>
    );
  }

  const script = current.data;

  // The route redirects before the scenes are confirmed, so this is a URL typed
  // against a production that lost its screenplay — say so.
  if (!script) {
    return (
      <p className="mm" style={{ fontSize: 13 }} data-testid="elements-step-empty">
        There is no screenplay to break down yet.
      </p>
    );
  }

  return (
    <div data-testid="elements-step">
      <ElementsWorkspace
        projectId={projectId}
        scriptVersionId={script.scriptVersionId}
        versionNumber={script.versionNumber}
        address={here}
        navigate={navigate}
        onBackToWorkflows={() => {
          // The choice reads the server's answer; make sure it is this one.
          void queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }).then(onDone);
        }}
        onOpenLocationRequirement={onOpenLocationRequirement}
      />
    </div>
  );
}

/** The detailed breakdown with its state in the router's address — what the `/elements` route renders. */
export function ElementsStepAtAddress(props: {
  projectId: string;
  projectTitle: string;
  onDone: () => void;
  onOpenLocationRequirement?: (requirementId: string) => void;
}) {
  const [address, navigate] = useRoutedElementsAddress();

  return <ElementsStep {...props} address={address} onAddressChange={navigate} />;
}
